import React, { useEffect, useState, useCallback } from 'react';
import { AdminNav, AdminTab } from '../components/admin/AdminNav';
import { OverviewSection } from '../components/admin/OverviewSection';
import { LovePhrasesSection } from '../components/admin/LovePhrasesSection';
import { NicknamesSection } from '../components/admin/NicknamesSection';
import { AccountsSection } from '../components/admin/AccountsSection';
import { SettingsSection } from '../components/admin/SettingsSection';
import { SessionsSection } from '../components/admin/SessionsSection';
import { BackupSection } from '../components/admin/BackupSection';
import { LivePreviewModal } from '../components/admin/LivePreviewModal';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import type {
  AdminAccount,
  AdminContentItem,
  AdminSessionItem,
  HeartSettings,
} from '../lib/types';

export const AdminPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [currentTab, setCurrentTab] = useState<AdminTab>('overview');
  const [contentSubTab, setContentSubTab] = useState<'phrases' | 'nicknames'>('phrases');

  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Admin data states
  const [settings, setSettings] = useState<HeartSettings | null>(null);
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [lovePhrases, setLovePhrases] = useState<AdminContentItem[]>([]);
  const [nicknames, setNicknames] = useState<AdminContentItem[]>([]);
  const [sessions, setSessions] = useState<AdminSessionItem[]>([]);

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSettings, setPreviewSettings] = useState<HeartSettings | null>(null);

  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const [settingsRes, accountsRes, contentRes, sessionsRes] = await Promise.all([
        api.getAdminSettings(),
        api.getAdminAccounts(),
        api.getAdminContent(),
        api.getAdminSessions(),
      ]);

      setSettings(settingsRes);
      setAccounts(accountsRes.accounts);
      setLovePhrases(contentRes.lovePhrases);
      setNicknames(contentRes.nicknames);
      setSessions(sessionsRes.sessions);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Handlers for Phrases
  const handleAddPhrase = async (phrase: string, language: string) => {
    await api.createAdminContent({ type: 'love', text: phrase, language });
    const contentRes = await api.getAdminContent();
    setLovePhrases(contentRes.lovePhrases);
  };

  const handleUpdatePhrase = async (id: string, phrase: string, language: string, isActive: boolean) => {
    await api.updateAdminContent({ type: 'love', id, text: phrase, language, isActive });
    const contentRes = await api.getAdminContent();
    setLovePhrases(contentRes.lovePhrases);
  };

  const handleDeletePhrase = async (id: string) => {
    await api.deleteAdminContent('love', id);
    const contentRes = await api.getAdminContent();
    setLovePhrases(contentRes.lovePhrases);
  };

  // Handlers for Nicknames
  const handleAddNickname = async (nickname: string) => {
    await api.createAdminContent({ type: 'nickname', text: nickname });
    const contentRes = await api.getAdminContent();
    setNicknames(contentRes.nicknames);
  };

  const handleUpdateNickname = async (id: string, nickname: string, isActive: boolean) => {
    await api.updateAdminContent({ type: 'nickname', id, text: nickname, isActive });
    const contentRes = await api.getAdminContent();
    setNicknames(contentRes.nicknames);
  };

  const handleDeleteNickname = async (id: string) => {
    await api.deleteAdminContent('nickname', id);
    const contentRes = await api.getAdminContent();
    setNicknames(contentRes.nicknames);
  };

  // Handlers for Accounts
  const handleUpdateAccount = async (data: {
    accountId: string;
    email?: string;
    newPassword?: string;
    isActive?: boolean;
  }) => {
    await api.updateAdminAccount(data);
    const accountsRes = await api.getAdminAccounts();
    setAccounts(accountsRes.accounts);
  };

  // Handlers for Settings
  const handleSaveSettings = async (newSettings: HeartSettings) => {
    const res = await api.updateAdminSettings(newSettings);
    setSettings(res.settings);
  };

  // Handlers for Sessions
  const handleRevokeSession = async (sessionId: string) => {
    await api.revokeAdminSession(sessionId);
    const sessionsRes = await api.getAdminSessions();
    setSessions(sessionsRes.sessions);
  };

  const handleRevokeAllSessions = async (keepCurrent: boolean) => {
    await api.revokeAllAdminSessions(keepCurrent);
    if (!keepCurrent) {
      await logout();
    } else {
      const sessionsRes = await api.getAdminSessions();
      setSessions(sessionsRes.sessions);
    }
  };

  const openPreview = (customSettings?: HeartSettings) => {
    setPreviewSettings(customSettings || settings);
    setPreviewOpen(true);
  };

  if (isLoading) {
    return (
      <div className="admin-loading-screen" role="status">
        <div className="subtle-spinner" />
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminNav
        currentTab={currentTab}
        onTabChange={setCurrentTab}
        onOpenPreview={() => openPreview()}
        onLogout={logout}
      />

      <main className="admin-main-content">
        {errorMsg && (
          <div className="notice-banner banner-danger" role="alert">
            {errorMsg}
          </div>
        )}

        {currentTab === 'overview' && (
          <OverviewSection
            settings={settings}
            accounts={accounts}
            lovePhrases={lovePhrases}
            nicknames={nicknames}
            sessions={sessions}
            onOpenPreview={() => openPreview()}
            onNavigateTab={tab => setCurrentTab(tab as AdminTab)}
          />
        )}

        {currentTab === 'content' && (
          <div className="admin-section">
            <div className="section-header">
              <h2>Content Library</h2>
              <p className="section-description">
                Cura multilinguale Liebesbekundungen und Kosenamen.
              </p>
              <div className="sub-tabs-bar">
                <button
                  type="button"
                  className={`sub-tab-btn ${contentSubTab === 'phrases' ? 'active' : ''}`}
                  onClick={() => setContentSubTab('phrases')}
                >
                  Love Phrases ({lovePhrases.length})
                </button>
                <button
                  type="button"
                  className={`sub-tab-btn ${contentSubTab === 'nicknames' ? 'active' : ''}`}
                  onClick={() => setContentSubTab('nicknames')}
                >
                  Nicknames ({nicknames.length})
                </button>
              </div>
            </div>

            {contentSubTab === 'phrases' ? (
              <LovePhrasesSection
                phrases={lovePhrases}
                onAddPhrase={handleAddPhrase}
                onUpdatePhrase={handleUpdatePhrase}
                onDeletePhrase={handleDeletePhrase}
              />
            ) : (
              <NicknamesSection
                nicknames={nicknames}
                onAddNickname={handleAddNickname}
                onUpdateNickname={handleUpdateNickname}
                onDeleteNickname={handleDeleteNickname}
              />
            )}
          </div>
        )}

        {currentTab === 'accounts' && user && (
          <AccountsSection
            accounts={accounts}
            currentAdminId={user.id}
            onUpdateAccount={handleUpdateAccount}
          />
        )}

        {currentTab === 'settings' && settings && (
          <SettingsSection
            initialSettings={settings}
            onSave={handleSaveSettings}
            onPreviewWithSettings={s => openPreview(s)}
          />
        )}

        {currentTab === 'sessions' && (
          <SessionsSection
            sessions={sessions}
            onRevokeSession={handleRevokeSession}
            onRevokeAllSessions={handleRevokeAllSessions}
          />
        )}

        {currentTab === 'backup' && (
          <BackupSection
            onImportComplete={async () => {
              const contentRes = await api.getAdminContent();
              setLovePhrases(contentRes.lovePhrases);
              setNicknames(contentRes.nicknames);
            }}
          />
        )}
      </main>

      {/* Live Preview Modal */}
      {previewOpen && previewSettings && (
        <LivePreviewModal
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          settings={previewSettings}
          lovePhrases={lovePhrases}
          nicknames={nicknames}
        />
      )}
    </div>
  );
};
